import React, { useMemo } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { Box, Typography } from '@mui/material'

import poliBidirectionalAnimationRaw from '~assets/lottie/poli-bidirectional-animation_32px.json?raw'
import {
  DEFAULT_PRIMARY_HEX,
  DEFAULT_SECONDARY_HEX,
  recolorLottie,
  tintHex,
} from '~utils/lottieColorUtils'

const poliBidirectionalAnimation = JSON.parse(poliBidirectionalAnimationRaw)

let _defaultAnimationData: string | null = null
const getDefaultAnimationData = (): string => {
  if (!_defaultAnimationData) {
    _defaultAnimationData = JSON.stringify(
      recolorLottie(poliBidirectionalAnimation, DEFAULT_PRIMARY_HEX, DEFAULT_SECONDARY_HEX),
    )
  }
  return _defaultAnimationData
}

const orgAnimationCache = new Map<string, string>()
const getOrgAnimationData = (primary: string, secondary: string): string => {
  const key = `${primary}|${secondary}`
  if (!orgAnimationCache.has(key)) {
    orgAnimationCache.set(
      key,
      JSON.stringify(recolorLottie(poliBidirectionalAnimation, primary, secondary)),
    )
  }
  return orgAnimationCache.get(key)!
}

interface AILoadingAnimationProps {
  width?: number
  height?: number
  minHeight?: number
  orgColor?: string
  orgColorSecondary?: string
  statusText?: string | null
  fontSize?: string | number
  fontFamily?: string
  color?: string
  alignLeft?: boolean
}

const AILoadingAnimation: React.FC<AILoadingAnimationProps> = ({
  width = 64,
  height = 64,
  minHeight = 140,
  orgColor,
  orgColorSecondary,
  statusText,
  fontSize = '0.75rem',
  fontFamily,
  color = 'text.secondary',
  alignLeft = false,
}) => {
  const animationData = useMemo(() => {
    if (!orgColor) return getDefaultAnimationData()
    const secondary = orgColorSecondary ?? tintHex(orgColor, 0.45)
    return getOrgAnimationData(orgColor, secondary)
  }, [orgColor, orgColorSecondary])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: alignLeft ? 'flex-start' : 'center',
        minHeight,
        minWidth: 0,
        overflow: 'visible',
        gap: 1,
      }}
    >
      <DotLottieReact data={animationData} loop autoplay style={{ width, height, flexShrink: 0 }} />
      {statusText != null && (
        <Typography
          component='span'
          variant='body2'
          noWrap
          sx={{ color, fontSize, fontFamily, minWidth: 0 }}
        >
          {statusText}
        </Typography>
      )}
    </Box>
  )
}

export default AILoadingAnimation
